import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, switchMap, BehaviorSubject, of } from 'rxjs';
import { tap, catchError, take, map, filter } from 'rxjs/operators';
import { AuthService } from 'src/app/core/auth/services/auth.service';
import { StorageService } from 'src/app/core/services/storage.service';
import { User } from 'src/app/core/interfaces/user';
import { environment } from 'src/environments/environments';
import { AppContextService } from './app-context.service';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class UsersService {

  private usersBaseUrl = environment.usersBaseUrl;
  private imagesBaseUrl = environment.imagesBaseUrl;
  private organizationsBaseUrl = environment.organizationsBaseUrl;
  public loading: boolean = false;
  private userSubject = new BehaviorSubject<any | null>(null);
  public user$ = this.userSubject.asObservable();

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private storageService: StorageService,
    private appContext: AppContextService,
    private router: Router
  ) {}
  
  loadUser(): Observable<User | null> {
    if (!this.authService.authenticated()) {
      return of(null);
    }

    const url = new URL(window.location.href);
    const isMoodle = url.searchParams.has('moodle');
    this.appContext.setMoodleMode(isMoodle);

    return this.http.get<any[]>(`${this.usersBaseUrl}/getUserToken`, { 
      headers: { 'Authorization': 'Bearer ' + this.authService.getToken() }
    }).pipe(
      take(1),
      map(res => {
        if (!res || res.length === 0) {
          if (this.appContext.isMoodle) {
            this.router.navigate(['/moodle-error']).then(() => {
              this.authService.clearSession();
            });
          } else{
            this.authService.logout();
          }
          return null;
        }

        const organizations = res
        .filter(elem => !!elem.group)
        .map(elem => ({
          id: elem.organizationId,
          name: elem.organizationName,
          group: elem.group
        }));

        if (organizations.length === 0) {
          if (this.appContext.isMoodle) {
            this.router.navigate(['/moodle-error']).then(() => {
              this.authService.clearSession();
            });
          } else{
            this.authService.logout();
          }
          return null;
        }

        let selectedOrganization = this.storageService.get('selectedOrganization');
        const isValidOrg = organizations.some(org => org.id === selectedOrganization);

        if (!isValidOrg) {
          if (this.appContext.isMoodle) {
            this.router.navigate(['/moodle-error']).then(() => {
              this.authService.clearSession();
            });
          } 
          selectedOrganization = organizations[0]?.id;
        }

        if (selectedOrganization) {
          this.storageService.set("selectedOrganization", selectedOrganization);
        }

        const userData = res.find(elem => elem.organizationId === selectedOrganization) || res[0];

        const user: User = {
          ...userData,
          birthdate: userData.birthdate ? new Date(userData.birthdate).toISOString().split('T')[0] : undefined,
          organizations: organizations
        };

        this.userSubject.next(user);

        return user;
      }),
      catchError((error) => {
        console.log(error)
        if (this.appContext.isMoodle) {
          this.router.navigate(['/moodle-error']).then(() => {
              this.authService.clearSession();
            });
        } else {
          this.authService.logout();
        }

        return of(null);
      })
    );
  }

  getUserData$(): Observable<User> {
    return this.user$.pipe(
      filter(user => !!user),
      take(1)
    );
  }

  getUserData(): User | null {
    return this.userSubject.getValue();
  }

  isUserDataLoaded(): boolean {
    return this.userSubject.getValue() !== null;
  }

  getGroup$(): Observable<string> {
    return this.user$.pipe(
      filter(user => !!user),
      take(1),
      map(user => user!.group)
    );
  }

  getGroup(): string | null {
    return this.userSubject.getValue()?.group ?? null;
  }

  getOrganization(): string | null {
    return this.userSubject.getValue()?.organizationId ?? null;
  }

  patchUserData(partialUser: Partial<User>): void {
    const currentUser = this.getUserData();
    if (currentUser) {
      const updatedUser: User = {
        ...currentUser,
        ...partialUser
      };
      this.userSubject.next(updatedUser);
    }
  }

  setUserData(user: User): void {
    this.storageService.set('selectedOrganization', user.organizationId!);
    this.userSubject.next(user);
  }

  clearUserData(): void {
    this.userSubject.next(null);
  }

  getStudents(lessonId: string): Observable<any> {
    return this.http.get(`${this.usersBaseUrl}/studentsByLesson/${lessonId}`)
  }

  /*getOrganizationId(): Observable<any> {
    const userId = this.authService.getUserId();
    return this.http.get(`${this.organizationsBaseUrl}/byUserId/${userId}`)
  }*/

  inviteStudents(
    courseLessonId: string,
    lessonTitle: string,
    userEmails: string[],
    initDate: string,
    endDate: string
  ): Observable<any> {
    const organizationId = this.storageService.get('selectedOrganization');

    return this.http.post(`${this.usersBaseUrl}/sendLessonInvite/${courseLessonId}`, {
      lessonTitle,
      userEmails,
      initDate,
      endDate,
      organizationId
    }, { headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + this.authService.getToken(),
    }});
  }

  revokeInvite(courseLessonId: string, userEmail: string): Observable<any> {
    return this.http.post(`${this.usersBaseUrl}/removeLessonInvite/${courseLessonId}`, {userEmail})
  }

  checkInvite(lessonId: string, email: string) {
    return this.http.get(`https://${environment.baseUrl}/php/checkInvite.php?lessonId=${lessonId}&email=${email}`, {observe: "response"})
  }

  getUsers(){
    return this.http.get(`${this.usersBaseUrl}/getUsers`, {observe: "response"})
  }

  getUsersFilter(organizationId?: string): Observable<any>{
    const params = organizationId ? `?organizationId=${organizationId}` : '';

    return this.http.get(`${this.usersBaseUrl}/getUsersFilter${params}`)
  }

  getUserByEmail(email: string): Observable<any>{
    // console.log("getUserByEmail", this.authService.getToken())
    return this.http.post(`${this.usersBaseUrl}/getUser`, {email}, { headers: {
                                                                  'Content-Type': 'application/json',
                                                                  'Authorization': 'Bearer ' + this.authService.getToken(),
                                                                }})
  }

  getUser(): Observable<any>{
    // console.log("getUser", this.authService.getToken())
    return this.http.get(`${this.usersBaseUrl}/getUserToken`, { headers: {
                                                                  'Content-Type': 'application/json',
                                                                  'Authorization': 'Bearer ' + this.authService.getToken(),
                                                                }})
  }

  changeUserRole(userId: string, organizationId: string, group: string): Observable<any>{
    return this.http.post(`${this.usersBaseUrl}/changeUserRole`, {userId, organizationId, group}, { headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + this.authService.getToken(),
    }})
  }

  deleteUserOrganization(userId: string, organizationId: string): Observable<any>{
    return this.http.post(`${this.usersBaseUrl}/deleteUserOrganization`, {userId, organizationId}, { headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + this.authService.getToken(),
    }})
  }

  updateUser(data: User): Observable<any> {
    const organizationId = this.getUserData()?.organizationId;
    return this.http.put(this.usersBaseUrl, {...data, organizationId}, { headers: {
                                                      'Content-Type': 'application/json',
                                                      'Authorization': 'Bearer ' + this.authService.getToken(),
                                                    }});
  }

  saveUserDynamo(email: string, courseLessonId: string): Observable<any> {
    return this.http.post(`${this.usersBaseUrl}/saveUserDynamo`, {email, courseLessonId});
  }

  updateProfileImage(file: string, extension: string): Observable<any> {
    const organizationId = this.getUserData()?.organizationId;
    return this.http
      .post(`${this.imagesBaseUrl}/uploadProfilePicture`, {
        extension,
        file,
        organizationId
      }, { headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + this.authService.getToken(),
      }})
  }

  getUserInfo(userId: string){
    return this.http.get(`${this.usersBaseUrl}/getUserInfo/${userId}`, {observe: "response"})
  }
}

